// src/components/about/EnhancedPartnersSection.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaGlobe, FaHandshake, FaExternalLinkAlt, FaGraduationCap } from 'react-icons/fa';
import Section from '../common/Section';
import Heading from '../common/Heading';
import Button from '../common/Button';

const EnhancedPartnersSection = ({ background = "gray" }) => {
  const [activePartner, setActivePartner] = useState(null);
  
  // Partners data
  const partners = [
    {
      id: 'ewb',
      name: 'Engineers Without Borders – Denmark',
      logo: '/images/partners/Engineers Without Borders - Denmark.png',
      placeholderText: 'EWB-DK',
      category: 'international',
      description: 'A key international partner supporting Leotech with technical expertise and resources for sustainable engineering skills development.',
      website: 'https://www.iug.dk/',
      relationship: 'Strategic capacity building partner focused on sustainable development projects'
    },
    {
      id: 'csid',
      name: 'Civil Society in Development – Denmark',
      logo: '/images/partners/Civil Society in Development - Denmark.jpg',
      placeholderText: 'CSID-DK',
      category: 'international',
      description: 'Provides guidance on civil society development and social enterprise models for sustainable impact.',
      website: 'https://www.cisu.dk/',
      relationship: 'Advisory partner for organizational development and sustainable funding models'
    },
    {
      id: 'slie',
      name: 'Sierra Leone Institution of Engineers',
      logo: '/images/partners/slie.png',
      placeholderText: 'SLIE',
      category: 'local',
      description: 'The professional body for engineers in Sierra Leone, providing industry recognition and technical support for Leotech programs.',
      website: '#',
      relationship: 'Professional accreditation and industry standards alignment'
    },
    {
      id: 'efcsl',
      name: 'Engineers for Change Sierra Leone',
      logo: '/images/partners/efcsl.png',
      placeholderText: 'EFCSL',
      category: 'local',
      description: 'A network of engineers dedicated to creating positive change through engineering solutions in Sierra Leone.',
      website: '#',
      relationship: 'Knowledge sharing and collaborative field projects implementation'
    },
    {
      id: 'slwe',
      name: 'Sierra Leone Women Engineers',
      logo: '/images/partners/slwe.png',
      placeholderText: 'SLWE',
      category: 'local',
      description: 'Promotes gender diversity in engineering fields and supports Leotech in encouraging female participation in technical programs.',
      website: 'https://www.slwe.org.sl/',
      relationship: 'Gender inclusion initiatives and female student mentorship programs'
    },
    {
      id: 'freetown-poly',
      name: 'Freetown Innovation Lab',
      logo: '/images/partners/freetown-poly.png',
      placeholderText: 'FP',
      category: 'local',
      description: 'An academic institution collaborating with Leotech to enhance practical skills training for engineering students.',
      website: '#',
      relationship: 'Student referrals and joint certification programs'
    },
    {
      id: 'fbc',
      name: 'Engineering Society - FBC',
      logo: '/images/partners/Engineering Society - FBC.png',
      placeholderText: 'FBC',
      category: 'academic',
      description: 'Sierra Leone\'s premier university working with Leotech to complement theoretical education with practical skills.',
      website: '#',
      relationship: 'Academic integration and curriculum enhancement support'
    },
    // {
    //   id: 'mmtu',
    //   name: 'Milton Margai Technical University',
    //   logo: '/images/partners/mmtu.png',
    //   placeholderText: 'MMTU',
    //   category: 'academic',
    //   description: 'A technical university partnering with Leotech to bridge the gap between academic education and industry skills.',
    //   website: '#',
    //   relationship: 'Industry-academia linkage and graduate skills enhancement'
    // }
  ];
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  // Filter categories
  const [activeCategory, setActiveCategory] = useState('all');
  const categories = [
    { id: 'all', name: 'All Partners' },
    { id: 'international', name: 'International' },
    { id: 'local', name: 'Local' },
    { id: 'academic', name: 'Academic' }
  ];

  // Filter partners based on active category
  const filteredPartners = activeCategory === 'all' 
  ? partners 
  : partners.filter(partner => partner.category === activeCategory);

return (
  <Section background={background} padding="lg">
    <div className="text-center mb-12">
      <Heading level={2} align="center" className="mb-4" withAccent>
        Our Partners & Collaborators
      </Heading>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">
        Leotech collaborates with leading organizations locally and internationally to provide the best training and opportunities for our students
      </p>
    </div>
    
    {/* Category filters */}
    {/* <div className="flex flex-wrap justify-center mb-12 gap-3">
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => setActiveCategory(category.id)}
          className={`px-4 py-2 rounded-full font-medium transition-colors ${
            activeCategory === category.id 
              ? 'bg-primary text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {category.name}
        </button>
      ))}
    </div> */}
    
    {/* Partners grid */}
    <motion.div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {filteredPartners.map((partner) => (
        <motion.div
          key={partner.id}
          variants={itemVariants}
          className={`bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-all duration-300 hover:shadow-lg ${
            activePartner === partner.id ? 'ring-2 ring-primary scale-105' : 'hover:-translate-y-2'
          }`}
          onClick={() => setActivePartner(activePartner === partner.id ? null : partner.id)}
        >
          <div className="h-32 flex items-center justify-center p-4 border-b border-gray-100">
            <div className="h-full max-w-full flex items-center justify-center">
              <img
                src={partner.logo}
                alt={partner.name}
                className="max-h-full max-w-full object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.parentElement.innerHTML = `
                    <div class="h-16 w-full bg-primary-dark text-white text-xl font-bold flex items-center justify-center rounded-md">
                      ${partner.placeholderText}
                    </div>
                  `;
                }}
              />
            </div>
          </div>
          
          <div className="p-4">
            <span className="inline-block text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-1 mb-2">
              {partner.category === 'international' ? 'International' : 
               partner.category === 'local' ? 'Local Organization' : 'Academic Institution'}
            </span>
            <h5 className="font-bold text-primary-dark mb-2 line-clamp-2 min-h-[48px]">{partner.name}</h5>
            
            {activePartner === partner.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-gray-600 text-sm mb-3">{partner.description}</p>
                
                <div className="bg-gray-50 p-3 rounded-md mb-3">
                  <div className="flex items-start">
                    <FaHandshake className="text-primary mt-1 mr-2 flex-shrink-0" />
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-1">Partnership Focus:</h4>
                      <p className="text-xs text-gray-600">{partner.relationship}</p>
                    </div>
                  </div>
                </div>
                
                {partner.website && partner.website !== '#' && (
                  <a 
                    href={partner.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary font-medium hover:text-primary-dark"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaGlobe className="mr-1" /> Visit Website <FaExternalLinkAlt className="ml-1 text-xs" />
                  </a>
                )}
              </motion.div>
            )}
            
            {activePartner !== partner.id && (
              <button className="text-primary text-sm font-medium">Click to learn more</button>
            )}
          </div>
        </motion.div>
      ))}
    </motion.div>
    
    {/* Partnership types section */}
    <div className="mt-16">
      <div className="bg-white rounded-lg shadow-md p-8">
        <Heading level={3} className="mb-6 text-center">
          Types of Partnerships
        </Heading>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-blue-50 rounded-lg p-6 border-t-4 border-blue-500">
            <div className="h-14 w-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-2xl mb-4">
              <FaGlobe />
            </div>
            <h4 className="font-bold text-primary-dark mb-2">International Partners</h4>
            <p className="text-gray-700 text-sm">
              Global organizations that provide technical expertise, funding, and international best practices to enhance our training programs.
            </p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6 border-t-4 border-green-500">
            <div className="h-14 w-14 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-2xl mb-4">
              <FaHandshake />
            </div>
            <h4 className="font-bold text-primary-dark mb-2">Local Organizations</h4>
            <p className="text-gray-700 text-sm">
              Sierra Leonean organizations that help ensure our training is aligned with local market needs and provide networking opportunities.
            </p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-6 border-t-4 border-purple-500">
            <div className="h-14 w-14 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-2xl mb-4">
              <FaGraduationCap />
            </div>
            <h4 className="font-bold text-primary-dark mb-2">Academic Institutions</h4>
            <p className="text-gray-700 text-sm">
              Universities and technical institutions that collaborate with us to bridge the gap between academic education and practical skills.
            </p>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Interested in partnering with Leotech? We're always looking for new collaborations that can enhance our mission of empowering youth through digital skills.
          </p>
          <Button 
            variant="primary" 
            to="/contact" 
          >
            Become a Partner
          </Button>
        </div>
      </div>
    </div>
  </Section>
);
};

export default EnhancedPartnersSection;